import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";

/**
 * Phone Workflow API.
 *
 * Talks to the Express middleware at `/api/phone-workflows`. The middleware
 * resolves the user from the session cookie and forwards to the Workflow
 * service, which internally handles saving/unlocking contacts. Separate
 * from workflowApi.ts (the email waterfall) - the phone-finding waterfall
 * tries phone providers in rank order with no verification checkpoint, so
 * there's nothing to check an unconfirmed find against and no name is
 * required to create one.
 */

export interface CreatePhoneWorkflowPayload {
  personId: string;
  companyId: string;
}

export interface PhoneWorkflowSnapshotAttempt {
  rank: number;
  providerKey: string;
  providerName: string;
  creditCost: number;
  dispatchState: "dispatched" | "resolved";
  /**
   * This provider's own verdict. Optional because older snapshots predate it;
   * the retry endpoints always populate it, and it is the only place a
   * per-provider "credit_exhausted" is reported.
   */
  status?: "found" | "not_found" | "credit_exhausted";
  phone: string | null;
  dispatchedAt: string;
  resolvedAt: string | null;
}

export interface PhoneWorkflowProviderOrderEntry {
  providerKey: string;
  providerName: string;
  creditCost: number;
}

/**
 * POST /api/phone-workflows and GET /api/phone-workflows/:id's `data` shape
 * - the full current PhoneWorkflowView, used both to seed Redux right after
 * creation and to re-sync it after the Notification-Service stream
 * reconnects (see sidebar.tsx).
 */
/**
 * Retry payloads - the phone twin of workflowApi's. These mint their own
 * workflow rather than re-running an existing one; see the note there.
 *
 * Unlike email, phone has no verification checkpoint, so both endpoints always
 * resolve inside the request - there is no `in_progress` response to chase on
 * the SSE stream.
 */
export interface RetryPhoneWorkflowIdentity {
  personId: string;
  companyId: string;
  source?: "radar" | "workspace" | "enrichment";
  /** Required when source is "workspace" or "enrichment". */
  docId?: string;
  /** Required when source is "workspace". */
  columnName?: string;
}

export interface TryPhoneProviderPayload extends RetryPhoneWorkflowIdentity {
  providerKey: string;
}

export interface TryAllPhoneProvidersPayload extends RetryPhoneWorkflowIdentity {
  /** 1-20 unique keys. */
  providerKeys: string[];
}

export interface PhoneWorkflowSnapshot {
  _id: string;
  userId: string;
  category: "phone";
  personId: string;
  companyId: string;
  providerOrder: PhoneWorkflowProviderOrderEntry[];
  currentRank: number;
  attempts: PhoneWorkflowSnapshotAttempt[];
  phone: string | null;
  /** ISO timestamp once the waterfall stops - null while in progress. */
  completedAt: string | null;
  /** Phone has no verification checkpoint, so a retry always comes back completed. */
  status?: "completed" | "in_progress";
  createdAt: string;
  updatedAt: string;
}

export interface BulkPhoneWorkflowItem {
  personId: string;
  companyId: string;
}

export interface CreateBulkPhoneWorkflowPayload {
  items: BulkPhoneWorkflowItem[];
  source: "radar" | "workspace";
}

/**
 * POST /api/phone-workflows/bulk and GET /api/phone-workflows/bulk/:id's
 * `data` shape - an aggregate doc, same reasoning as workflowApi.ts's
 * BulkWorkflowSnapshot (no per-item breakdown at all).
 */
export interface BulkPhoneWorkflowSnapshot {
  _id: string;
  userId: string;
  category: "phone";
  source: "radar" | "workspace";
  total: number;
  duplicates: number;
  counts: {
    completed: number;
    found: number;
    notFound: number;
    creditExhausted: number;
  };
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export const phoneWorkflowApi = createApi({
  reducerPath: "phoneWorkflowApi",
  baseQuery: axiosBaseQuery("/api/phone-workflows"),
  tagTypes: ["PhoneWorkflow", "BulkPhoneBatches"],
  endpoints: (builder) => ({
    createPhoneWorkflow: builder.mutation<{ success: boolean; message?: string; data: PhoneWorkflowSnapshot }, CreatePhoneWorkflowPayload>({
      query: (payload) => ({
        // Deliberately "" not "/" - a trailing slash was found to cause a
        // routing/validation issue and was fixed everywhere else in this
        // codebase (see workflowApi.ts).
        url: "",
        method: "POST",
        body: payload,
      }),
    }),
    getPhoneWorkflow: builder.query<PhoneWorkflowSnapshot, string>({
      query: (id) => ({ url: `/${id}`, method: "GET" }),
      transformResponse: (response: { data: PhoneWorkflowSnapshot }) => response.data,
    }),
    createBulkPhoneWorkflow: builder.mutation<{ success: boolean; message?: string; data: BulkPhoneWorkflowSnapshot }, CreateBulkPhoneWorkflowPayload>({
      query: (payload) => ({
        url: "/bulk",
        method: "POST",
        body: payload,
      }),
    }),
    getBulkPhoneWorkflow: builder.query<BulkPhoneWorkflowSnapshot, string>({
      query: (id) => ({ url: `/bulk/${id}`, method: "GET" }),
      transformResponse: (response: { data: BulkPhoneWorkflowSnapshot }) => response.data,
    }),
    /**
     * This user's still-running phone batches, newest first - same
     * in-progress-only contract as workflowApi's listBulkWorkflows.
     */
    listBulkPhoneWorkflows: builder.query<BulkPhoneWorkflowSnapshot[], void>({
      query: () => ({ url: "/bulk", method: "GET" }),
      transformResponse: (response: { data: BulkPhoneWorkflowSnapshot[] }) => response.data ?? [],
      providesTags: ["BulkPhoneBatches"],
    }),

    /** Re-run ONE named phone provider, bypassing the saved waterfall order. */
    tryPhoneProvider: builder.mutation<{ success: boolean; message?: string; data: PhoneWorkflowSnapshot }, TryPhoneProviderPayload>({
      query: (payload) => ({ url: "/try", method: "POST", body: payload }),
    }),

    /** Re-run every listed phone provider concurrently. */
    tryAllPhoneProviders: builder.mutation<{ success: boolean; message?: string; data: PhoneWorkflowSnapshot }, TryAllPhoneProvidersPayload>({
      query: (payload) => ({ url: "/try-all", method: "POST", body: payload }),
    }),
  }),
});

export const {
  useCreatePhoneWorkflowMutation,
  useLazyGetPhoneWorkflowQuery,
  useCreateBulkPhoneWorkflowMutation,
  useLazyGetBulkPhoneWorkflowQuery,
  useListBulkPhoneWorkflowsQuery,
  useLazyListBulkPhoneWorkflowsQuery,
  useTryPhoneProviderMutation,
  useTryAllPhoneProvidersMutation,
} = phoneWorkflowApi;

export default phoneWorkflowApi;
